import { z } from 'zod';

import { resolveUploadedAssetFromRegistries } from '../registries/resolveUploadedAsset';
import { CustomRendererMode, type EnhancedToolResult } from '../types';

import { ConversationTool } from './ConversationTool';
import type { ToolConstructorArgs } from './Tool';

import type { ToolDefinition, ToolExecutionContext } from 'js/src/agent/types';
import { ApiService, AspectRatio, NarrativeThreadEnum, TranscriptAssetType } from 'js/src/client';
import { VideoPlanEditorRenderer } from 'js/src/components/video-planner/VideoPlanEditorRenderer';
import { getMaxVideoDurationSeconds } from 'js/src/data/account';
import {
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
    isImmutableAudioAsset,
} from 'js/src/data/media/media-types';
import { getUploadedAssetByUuid } from 'js/src/data/uploads/uploads-selectors';
import type { VoiceoverAsset } from 'js/src/data/video-planner/video-planner-types';

const MESSAGE_DEFAULT =
    "Here's your video plan from the uploaded voiceover. Review the transcript and adjust settings if needed, then click Create video.";

export const createVideoFromVoiceoverAssetSchema = z.object({
    audio_uuid: z
        .string()
        .describe(
            'The UUID of the uploaded audio or video file (from the AUDIO or MEDIA message metadata)',
        ),
    title: z.string().describe('A concise and catchy title for the video'),
    // The allowed template ids are per-instance (the templates available to this agent),
    // so the enum is injected in extendParameters rather than declared here.
    template_id: z
        .string()
        .describe(
            'Template ID to use from the available templates. MUST be one of the templates listed in the system message.',
        ),
    aspect_ratio: z.enum(AspectRatio).describe('The aspect ratio for the video'),
    content_prompt: z
        .string()
        .optional()
        .describe(
            'Optional visual guidance for scene recommendations - what kind of visuals should accompany the narration',
        ),
    blueprint_id: z.number().optional().describe('Optional blueprint ID for scene recommendations'),
    message: z.string().meta({
        description:
            "This message is shown to the user above the video outline. Translate the following sentence into the language the user is writing in. The sentence: 'Here's your video plan from the uploaded voiceover. Review the transcript and adjust settings if needed, then click Create video.'",
        default: MESSAGE_DEFAULT,
    }),
});
export type CreateVideoFromVoiceoverAssetArgs = z.infer<typeof createVideoFromVoiceoverAssetSchema>;

export interface CreateVideoFromVoiceoverAssetConstructorArgs extends ToolConstructorArgs {
    templateIds?: string[];
}

/**
 * Produces a voiceover-driven video outline from an uploaded audio or video file.
 * Does NOT build the video immediately — returns a VideoPlanWithVoiceoverAsset
 * that the VideoPlanEditor renders as an audio/video preview + transcript + editable
 * settings. The user clicks "Create video" to actually build, which calls
 * create_video_from_voiceover_asset_plan with the finalized settings.
 */
export class CreateVideoFromVoiceoverAssetTool extends ConversationTool<
    CreateVideoFromVoiceoverAssetArgs,
    CreateVideoFromVoiceoverAssetConstructorArgs
> {
    public static readonly toolName = 'create_video_from_voiceover_asset';

    protected override readonly schema = createVideoFromVoiceoverAssetSchema;
    protected override readonly toolName = CreateVideoFromVoiceoverAssetTool.toolName;
    protected override readonly toolDescription =
        'Build a video from an uploaded audio file or video with voiceover. The uploaded audio/video becomes the narration track, and matching visuals and scenes are generated automatically based on the transcript. Use this when the user uploads an audio file (MP3, WAV, etc.) or a video with speech and wants a video built around it.';

    public override canRunAsync = true;
    public override customRendererMode = CustomRendererMode.FULL;
    override customRenderer = VideoPlanEditorRenderer;
    private templateIds: string[];

    public constructor(args: CreateVideoFromVoiceoverAssetConstructorArgs) {
        super(args);
        this.templateIds = args.templateIds || [];
    }

    // template_id's allowed values are the templates available to this agent instance,
    // which the module-level schema can't see. Inject them as the enum the model chooses from.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        if (this.templateIds.length > 0) {
            (parameters.properties.template_id as { enum?: string[] }).enum = this.templateIds;
        }
        return parameters;
    }

    public override getToolDisplayName(): string {
        return 'Creating video outline';
    }

    public get statusIcon(): string {
        return 'movie';
    }

    protected override preprocessArgs(
        args: CreateVideoFromVoiceoverAssetArgs,
    ): CreateVideoFromVoiceoverAssetArgs {
        // message is required in the schema (with a default), but the model can omit it. Fill the
        // default here — preprocessArgs runs before validation — so a missing message doesn't hard-
        // fail, matching the Ajv useDefaults behavior the legacy (pre-Zod) path provided.
        if ((args as { message?: string }).message === undefined) {
            args = { ...args, message: MESSAGE_DEFAULT };
        }
        return args;
    }

    public async execute(
        args: CreateVideoFromVoiceoverAssetArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const state = this._getState();
        const uploadedAsset =
            getUploadedAssetByUuid(state, args.audio_uuid) ??
            (await resolveUploadedAssetFromRegistries(args.audio_uuid));

        if (!uploadedAsset) {
            return {
                result: 'Error: Could not find the uploaded audio/video file. The UUID may be incorrect or the upload may not have completed.',
                isSuccess: false,
            };
        }

        const isAudio = isImmutableAudioAsset(uploadedAsset);
        const isVideo =
            !isAudio && immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(uploadedAsset);

        if (!isAudio && !isVideo) {
            return {
                result: 'Error: The uploaded file is not a recognized audio or video asset.',
                isSuccess: false,
            };
        }

        // Fetch transcript; this also validates that the asset has speech
        let transcript = '';
        try {
            const transcriptResponse = await ApiService.assetTranscriptRetrieve({
                path: { asset_id: args.audio_uuid },
                query: {
                    asset_type: isAudio ? TranscriptAssetType.AUDIO : TranscriptAssetType.VIDEO,
                },
            });
            const words = transcriptResponse.data?.transcriptWords;
            if (!words || words.length === 0) {
                return {
                    result: `The uploaded ${isAudio ? 'audio' : 'video'} file doesn't appear to contain speech. Please upload a file with spoken narration to build a voiceover-driven video.`,
                    isSuccess: false,
                };
            }
            transcript = words
                .map((w) => w.word)
                .join(' ')
                .trim();
        } catch {
            return {
                result: `Could not retrieve transcript for the uploaded ${isAudio ? 'audio' : 'video'} file. The file may still be processing — please try again in a moment.`,
                isSuccess: false,
            };
        }

        const videoAsset = isAudio ? null : uploadedAsset.get('video');
        // AudioAsset.duration is in seconds; VideoAsset.duration is in milliseconds. Normalize
        // to seconds so the comparison against the plan's max (seconds) is correct — otherwise a
        // few-second clip reads as many minutes (e.g. 5700ms → "95 minutes").
        const rawVideoDuration = videoAsset?.get('duration');
        const durationSeconds = isAudio
            ? uploadedAsset.get('duration')
            : rawVideoDuration != null
              ? rawVideoDuration / 1000
              : undefined;
        const maxDuration = getMaxVideoDurationSeconds(state);
        if (durationSeconds && maxDuration && durationSeconds > maxDuration) {
            const maxMinutes = Math.floor(maxDuration / 60);
            const kindWord = isAudio ? 'audio' : 'video';
            return {
                result: `Error: The uploaded ${kindWord} is ${Math.round(durationSeconds)} seconds long, which exceeds your plan's maximum video duration of ${maxDuration} seconds (${maxMinutes} minutes). Please upload a shorter ${kindWord} file or upgrade your plan.`,
                isSuccess: false,
            };
        }

        const previewUrl = isAudio
            ? (uploadedAsset.get('mp3_url') ?? undefined)
            : (videoAsset?.get('preview_url') ?? undefined);

        const voiceoverAsset: VoiceoverAsset = {
            asset_uuid: args.audio_uuid,
            asset_kind: isAudio ? 'audio' : 'video',
            preview_url: previewUrl,
            transcript,
        };

        // Shape matches VideoPlanWithScript so VideoPlanEditorRenderer can consume it
        // via its `videoPlan` prop. Script field carries the transcript for display
        // fallback; the editor will prefer voiceover_asset when present.
        const videoPlan = {
            id: undefined,
            title: args.title,
            content_prompt: args.content_prompt ?? null,
            video_prompt: null,
            script: transcript,
            aspect_ratio: args.aspect_ratio,
            template_id: args.template_id,
            // Uploaded voiceover always drives the voiceover narrative thread
            narrative_thread: NarrativeThreadEnum.VOICEOVER,
            blueprint_id: args.blueprint_id ?? null,
            duration_target_seconds: null,
            ai_voice: null,
            ai_avatar: null,
            voiceover_asset: voiceoverAsset,
        };

        analytics.track('Sketch: Voiceover Video Plan Generated', {
            plan_title: args.title,
            template_id: args.template_id,
            aspect_ratio: args.aspect_ratio,
            asset_kind: voiceoverAsset.asset_kind,
            blueprint_id: args.blueprint_id,
        });

        return {
            result: JSON.stringify({ videoPlan }),
            isSuccess: true,
            stopConversation: true,
        };
    }
}
